import { CustomContent, CustomTranslations, ExperienceTranslation } from "@/types/generated/ExperienceRawServiceApi";
import { isEqual } from "lodash";
import { useCurrentTranslationDiffSnapshotsStore } from "@/features/experience-shared/stores/useCurrentTranslationDiffSnapshotsStore";
import { useIsCurrentTranslationAutomatic } from "@/features/experience-translation/composables/useIsCurrentTranslationAutomatic";

// ============================================================================
// TRANSLATION FIELDS
// ============================================================================

/**
 * Compares a translation field between current and last published English snapshot
 */
export const areTranslationFieldsUpdated = (translationField: keyof ExperienceTranslation): boolean => {
  const isAutomatic = useIsCurrentTranslationAutomatic();
  if (isAutomatic.value) return false;

  const store = useCurrentTranslationDiffSnapshotsStore();

  const currentEditorialContent = store.latestEnglishSnapshot?.english_translation?.experience_translation;
  const oldEditorialContent = store.lastEnglishSnapshotBeforePublication?.english_translation?.experience_translation;

  if (!oldEditorialContent || !currentEditorialContent) {
    return false;
  }
  return currentEditorialContent?.[translationField] !== oldEditorialContent?.[translationField];
};

/**
 * Compares custom translation fields using deep equality check
 */
export const areCustomTranslationFieldsUpdated = (customField: keyof CustomTranslations): boolean => {
  const isAutomatic = useIsCurrentTranslationAutomatic();
  if (isAutomatic.value) return false;

  const store = useCurrentTranslationDiffSnapshotsStore();

  const currentEditorialContent = store.latestEnglishSnapshot?.english_translation?.customs;
  const oldEditorialContent = store.lastEnglishSnapshotBeforePublication?.english_translation?.customs;

  if (!oldEditorialContent || !currentEditorialContent) {
    return false;
  }

  const sortByVisualizationOrder = (items: Array<CustomContent>) =>
    [...items].sort((a, b) => (a.visualization_order || 0) - (b.visualization_order || 0));

  const currentSorted = sortByVisualizationOrder(currentEditorialContent?.[customField] || []);
  const oldSorted = sortByVisualizationOrder(oldEditorialContent?.[customField] || []);

  // Use lodash deep equality check (handles empty arrays correctly)
  return !isEqual(currentSorted, oldSorted);
};

// ============================================================================
// OPTION NAMES FIELDS
// ============================================================================

/**
 * Compares option names between latest English snapshot and last published English snapshot
 * Returns true if any option name has changed
 */
export const areTranslationOptionNamesFieldsUpdated = (): boolean => {
  const isAutomatic = useIsCurrentTranslationAutomatic();
  if (isAutomatic.value) return false;

  const store = useCurrentTranslationDiffSnapshotsStore();

  const latestOptions = store.latestEnglishSnapshot?.english_translation?.options || [];
  const previousOptions = store.lastEnglishSnapshotBeforePublication?.english_translation?.options || [];

  // If both snapshots don't exist, no changes
  if (!store.latestEnglishSnapshot || !store.lastEnglishSnapshotBeforePublication) {
    return false;
  }

  // Check if any option name has changed
  return latestOptions.some((latestOption) => {
    const previousOption = previousOptions.find((prev) => prev.code === latestOption.code);

    return latestOption.name !== previousOption?.name;
  });
};

// ============================================================================
// OPERATIONAL FIELDS
// ============================================================================

/**
 * Compares the title of a specific operational translation between the latest
 * English snapshot and the last published English snapshot.
 * Returns true if the operational title has changed.
 */
export const areTranslationOperationalTitleFieldsUpdated = (operationalId: string): boolean => {
  const isAutomatic = useIsCurrentTranslationAutomatic();
  if (isAutomatic.value) return false;

  const store = useCurrentTranslationDiffSnapshotsStore();

  if (!store.latestEnglishSnapshot || !store.lastEnglishSnapshotBeforePublication) {
    return false;
  }

  const latestOp = store.latestEnglishSnapshot.english_translation?.operational_translations?.find(
    (t) => t.operational_id === operationalId
  );
  const previousOp = store.lastEnglishSnapshotBeforePublication.english_translation?.operational_translations?.find(
    (t) => t.operational_id === operationalId
  );

  if (!previousOp) return !!latestOp;

  return (latestOp?.name ?? "") !== (previousOp?.name ?? "");
};

/**
 * Compares option names for a specific operational between the latest
 * English snapshot and the last published English snapshot.
 * Returns true if any option name within the operational has changed.
 */
export const areTranslationOperationalOptionNamesFieldsUpdated = (optionCodes: string[]): boolean => {
  const isAutomatic = useIsCurrentTranslationAutomatic();
  if (isAutomatic.value) return false;

  const store = useCurrentTranslationDiffSnapshotsStore();

  if (!store.latestEnglishSnapshot || !store.lastEnglishSnapshotBeforePublication) {
    return false;
  }

  const latestOptions =
    store.latestEnglishSnapshot.english_translation?.options?.filter((o) => optionCodes.includes(o.code ?? "")) ?? [];
  const previousOptions =
    store.lastEnglishSnapshotBeforePublication.english_translation?.options?.filter((o) =>
      optionCodes.includes(o.code ?? "")
    ) ?? [];

  if (previousOptions.length === 0) return latestOptions.length > 0;

  if (latestOptions.length !== previousOptions.length) return true;

  return latestOptions.some((latest) => {
    const previous = previousOptions.find((p) => p.code === latest.code);
    return latest.name !== previous?.name;
  });
};
